// A status write must never be accepted by a server whose response contract
// the caller cannot understand. This version distinguishes pre-dispatch blocks,
// attempted writes, and acknowledgement from proof of the requested state.
export const ADS_MANAGER_STATUS_CONTRACT = "dispatch-aware-v4";
export const ADS_MANAGER_STATUS_RELOAD_ERROR = "Change not sent. Refresh the page to use the latest controls.";
export const ADS_MANAGER_CAMPAIGN_STATUS_UNAVAILABLE = "Current campaign status could not be verified for this account. No change was sent. Refresh status or open the campaign in Meta.";

export type AdsManagerStatusAcknowledgementOutcome = "explicit_success" | "opaque_ack";
export type AdsManagerStatusProviderOutcome = AdsManagerStatusAcknowledgementOutcome | "rejected" | "ambiguous";
/** Attempted means we crossed the provider-call boundary, not proof of receipt. */
export type AdsManagerStatusDispatchState = "not_sent" | "attempted";

export interface AdsManagerStatusBlockedResponse {
  statusContract: typeof ADS_MANAGER_STATUS_CONTRACT;
  dispatchState: "not_sent";
  outcome: "not_sent" | "reload_required";
  accepted: false;
  confirmed: false;
  error: string;
  nextAction: string;
  blockReason?: "entity_not_visible";
}

export function adsManagerStatusNotSent(value: {
  dispatchState?: AdsManagerStatusDispatchState;
  outcome?: string;
  accepted?: boolean;
  confirmed?: boolean;
  acknowledgement?: unknown;
} | null | undefined): boolean {
  return value?.dispatchState === "not_sent"
    && (value.outcome === "not_sent" || value.outcome === "reload_required")
    && value.accepted !== true && value.confirmed !== true && !value.acknowledgement;
}
export type AdsManagerStatusEvidenceSource =
  | "root"
  | "response"
  | "response_body"
  | "meta_response"
  | "provider_response"
  | "result"
  | "data"
  | "detail_transcript"
  | "http"
  | "transport";

/** Sanitized, allowlisted provider acknowledgement evidence; never includes a raw body. */
export interface AdsManagerStatusAcknowledgementEvidence {
  httpStatus: number | null;
  action: string | null;
  envelopeSource: AdsManagerStatusEvidenceSource;
  marker: "success_true" | "provider_success_text" | "opaque_2xx" | "provider_error" | "http_rejection" | "transport_uncertain" | "http_uncertain" | "not_live";
}

export interface AdsManagerStatusAcknowledgement {
  outcome: AdsManagerStatusProviderOutcome;
  evidence: AdsManagerStatusAcknowledgementEvidence;
}

export interface AdsManagerStatusReportingObservation {
  source: "windsor_reporting";
  state: "observed" | "unavailable";
  rawStatus: string | null;
  configuredStatus: string | null;
  effectiveStatus: string | null;
  retrievedAt: string | null;
  /** Windsor reporting freshness is undocumented, so it is never represented as live proof. */
  freshness: "unknown";
  reason?: string;
}

export interface AdsManagerStatusDirectVerification {
  source: "meta_graph";
  state: "verified" | "unavailable" | "unverified";
  configuredStatus: string | null;
  effectiveStatus: string | null;
  observedAt: string | null;
  agrees: boolean | null;
  reason?: string;
}

/** Windsor confirmation is provider success, distinct from independent Meta verification. */
export interface AdsManagerStatusWriteResponse {
  statusContract: typeof ADS_MANAGER_STATUS_CONTRACT;
  dispatchState: "attempted";
  outcome: "windsor_confirmed" | "unconfirmed";
  confirmed: boolean;
  confirmationSource: "windsor" | null;
  accepted: true;
  acknowledgement: AdsManagerStatusAcknowledgement;
  observation: AdsManagerStatusReportingObservation;
  verification: AdsManagerStatusDirectVerification;
}

export function adsManagerStatusContractRequired(body: { status?: unknown; statusContract?: unknown } | null | undefined): boolean {
  return typeof body?.status === "string"
    && body.status.trim() !== ""
    && body.statusContract !== ADS_MANAGER_STATUS_CONTRACT;
}